import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/date";
import { Newspaper, ExternalLink, Star, Search, ChevronLeft, ChevronRight, X } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Seo } from "@/components/Seo";

const PAGE_SIZE = 10;

export default function News() {
  return (
    <>
      <Seo
        title="West End theatre news"
        description="The latest West End headlines from trusted theatre news sources, matched to the shows and performers you follow."
      />
      <PageHero
        offset={2}
        overline="Aggregated from trusted sources"
        title="West End News"
        callout={{
          icon: Newspaper,
          lead: "Stay in the loop.",
          text: "Headlines are pulled from trusted theatre news feeds and refreshed every few hours. Stories about shows or performers you follow are marked on your watchlist.",
        }}
      />
      <NewsContent />
    </>
  );
}

function NewsContent() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => { setTerm(q.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    api.get("/news", { params: { q: term || undefined, page, page_size: PAGE_SIZE } })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0, pages: 1, page: 1 }));
  }, [term, page]);

  const items = data?.items;
  const pages = data?.pages || 1;
  const goTo = (p) => { setPage(Math.min(Math.max(1, p), pages)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">

      <div className="relative mt-8 max-w-lg">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          data-testid="news-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search headlines, shows, performers…"
          className="w-full rounded-full border border-input bg-card py-3 pl-10 pr-10 outline-none focus:ring-2 focus:ring-ring"
        />
        {q && (
          <button data-testid="news-search-clear" onClick={() => setQ("")} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {data && (
        <p data-testid="news-result-count" className="mt-3 text-sm text-muted-foreground">
          {data.total} {data.total === 1 ? "story" : "stories"}{term ? ` matching “${term}”` : ""}
          {data.total > 0 && ` · page ${data.page} of ${pages}`}
        </p>
      )}

      {items && items.length === 0 && (
        <div className="mt-8 rounded-2xl border border-dashed border-border bg-card p-12 text-center">
          <Newspaper className="mx-auto h-10 w-10 text-muted-foreground" />
          <h2 className="mt-4 font-serif text-2xl font-semibold">{term ? "No stories match" : "No news yet"}</h2>
          <p className="mx-auto mt-2 max-w-md text-muted-foreground">
            {term ? "Try a different word, show or performer name." : "News feeds are configured and refreshed by the admin from the Admin Panel."}
          </p>
        </div>
      )}

      <div className="mt-6 space-y-4">
        {items?.map((n, i) => (
          <a
            key={n.id}
            href={n.link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`news-item-${n.id}`}
            className={`fade-up block rounded-2xl border bg-card p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg ${
              n.followed_mention ? "border-[hsl(var(--gold))]" : "border-border"
            }`}
            style={{ animationDelay: `${i * 40}ms` }}
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="overline-label text-primary">{n.source}</span>
              {n.followed_mention && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--gold))]/15 px-2 py-0.5 text-xs font-semibold text-[hsl(var(--gold))]">
                  <Star className="h-3 w-3 fill-current" /> On your watchlist
                </span>
              )}
              <span className="ml-auto text-xs text-muted-foreground">{fmtDate(n.published)}</span>
            </div>
            <div className="mt-2 flex gap-4">
              {n.matched_show?.image_url && (
                <img
                  src={n.matched_show.image_url}
                  alt={n.matched_show.name}
                  className="h-20 w-14 flex-shrink-0 rounded-lg object-cover"
                />
              )}
              <div className="min-w-0 flex-1">
                <h3 className="flex items-start gap-2 font-serif text-xl font-semibold leading-snug">
                  {n.title}
                  <ExternalLink className="mt-1.5 h-4 w-4 shrink-0 text-muted-foreground" />
                </h3>
                {n.summary && <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{n.summary}</p>}
              </div>
            </div>
          </a>
        ))}
      </div>

      {pages > 1 && (
        <nav data-testid="news-pagination" className="mt-10 flex items-center justify-between border-t border-border pt-6">
          <button data-testid="news-prev-page" onClick={() => goTo(page - 1)} disabled={page <= 1} className="inline-flex items-center gap-1 rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-40">
            <ChevronLeft className="h-4 w-4" /> Previous
          </button>
          <span data-testid="news-page-indicator" className="font-serif text-sm text-muted-foreground">Page {page} of {pages}</span>
          <button data-testid="news-next-page" onClick={() => goTo(page + 1)} disabled={page >= pages} className="inline-flex items-center gap-1 rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-40">
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </nav>
      )}
    </div>
  );
}
